import { createTheme, alpha } from '@mui/material/styles';

const brand = {
  emerald: '#10B981',
  emeraldDark: '#059669',
  emeraldLight: '#D1FAE5',
  slate900: '#0F172A',
  slate800: '#1E293B',
  slate700: '#334155',
  slate500: '#64748B',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.emerald,
      dark: brand.emeraldDark,
      light: brand.emeraldLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.slate700,
    },
    success: {
      main: '#22C55E',
      light: '#DCFCE7',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
    },
    error: {
      main: '#EF4444',
      light: '#FEE2E2',
    },
    background: {
      default: brand.slate50,
      paper: '#FFFFFF',
    },
    text: {
      primary: brand.slate900,
      secondary: brand.slate500,
    },
    divider: brand.slate200,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    body2: {
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15, 23, 42, 0.05)',
    '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px -1px rgba(15, 23, 42, 0.08)',
    '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.06)',
    '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.06)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.06)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.08)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.slate50,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(brand.emerald, 0.35)}`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${brand.slate200}`,
          boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
        outlined: {
          borderColor: brand.slate200,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: brand.slate100,
            color: brand.slate700,
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: `1px solid ${brand.slate200}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
          '&:hover': {
            backgroundColor: alpha(brand.emerald, 0.04),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: brand.slate200,
          padding: '14px 16px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${brand.slate200}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export { brand };
